import { Helmet } from "react-helmet-async";
import { useLang } from "@/i18n/LanguageProvider";
import { SUPPORTED_LANGS } from "@/i18n/config";

const SITE = "https://dropycash.com";
const DEFAULT_HREFLANG_LANG = "fr";
const DEFAULT_SOCIAL_IMAGE = `${SITE}/blog-covers/dropshipping-cod-maroc.jpg`;
const DEFAULT_SOCIAL_IMAGE_ALT = "DropyCash, plateforme de dropshipping COD au Maroc";

const AR_HOME_TITLE = "DropyCash | منصة الدروبشيبينغ بالدفع عند الاستلام والتسويق بالعمولة";
const AR_HOME_DESCRIPTION =
  "ابدأ وطوّر تجارتك الإلكترونية مع DropyCash. منتجات رابحة، تأكيد طلبات احترافي، شحن سريع بالدفع عند الاستلام، وسحب أرباح للمسوقين والتجار. سجل الآن مجاناً.";

interface SEOProps {
  /** Unprefixed path, e.g. "/pricing" or "/". Language prefix is added automatically. */
  title: string;
  description: string;
  path?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  image?: string;
  imageAlt?: string;
  /** Emit robots noindex (drafts, noindex articles, fallback-locale pages). */
  noindex?: boolean;
  /** og:type, defaults to "website". */
  type?: "website" | "article";
  /** Override content language (e.g. article rendered in a fallback locale). */
  contentLang?: string;
  /**
   * Explicit hreflang alternates. When provided, only these are emitted
   * (avoids fake alternates for untranslated content).
   */
  alternates?: { lang: string; path: string }[];
  publishedTime?: string;
  modifiedTime?: string;
}

function joinPath(lang: string, path: string) {
  const clean = path === "/" ? "" : path.replace(/\/+$/, "");
  return `/${lang}${clean}`;
}

function ogLocale(lang: string) {
  if (lang === "ar") return "ar_MA";
  if (lang === "fr") return "fr_MA";
  return "en_US";
}

export function SEO({
  title,
  description,
  path = "/",
  jsonLd,
  image = DEFAULT_SOCIAL_IMAGE,
  imageAlt = DEFAULT_SOCIAL_IMAGE_ALT,
  noindex = false,
  type = "website",
  contentLang,
  alternates,
  publishedTime,
  modifiedTime,
}: SEOProps) {
  const { lang: uiLang } = useLang();
  const lang = contentLang ?? uiLang;
  const canonical = `${SITE}${joinPath(lang, path)}`;
  const alts =
    alternates ?? SUPPORTED_LANGS.map((l) => ({ lang: l as string, path: joinPath(l, path) }));
  const defaultAlt = alts.find((a) => a.lang === DEFAULT_HREFLANG_LANG) ?? alts[0];
  const defaultUrl = defaultAlt ? `${SITE}${defaultAlt.path}` : canonical;
  const absImage = image.startsWith("http") ? image : `${SITE}${image.startsWith("/") ? "" : "/"}${image}`;

  const isArabicHome = lang === "ar" && path === "/";
  const resolvedTitle = isArabicHome ? AR_HOME_TITLE : title;
  const resolvedDescription = isArabicHome ? AR_HOME_DESCRIPTION : description;

  const suppliedSchemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const schemas = isArabicHome
    ? [
        ...suppliedSchemas,
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "@id": `${SITE}/ar/#software`,
          name: "DropyCash",
          url: `${SITE}/ar`,
          applicationCategory: "BusinessApplication",
          operatingSystem: "All",
          inLanguage: "ar",
          description:
            "منصة متكاملة لإدارة عمليات الدروبشيبينغ بنظام الدفع عند الاستلام والتسويق بالعمولة في المغرب.",
        },
      ]
    : suppliedSchemas;

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={noindex ? "noindex,follow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"} />

      <link rel="canonical" href={canonical} />
      {!noindex &&
        alts.map((a) => <link key={a.lang} rel="alternate" hrefLang={a.lang} href={`${SITE}${a.path}`} />)}
      {!noindex && alts.length > 1 && <link rel="alternate" hrefLang="x-default" href={defaultUrl} />}

      <meta property="og:type" content={type} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      <meta property="og:site_name" content="DropyCash" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale(lang)} />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:alt" content={imageAlt} />
      {alts.filter((a) => a.lang !== lang).map(({ lang: l }) => (
        <meta key={l} property="og:locale:alternate" content={ogLocale(l)} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={absImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
